import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, CheckCircle2, ChevronLeft, ShoppingCart, ExternalLink, Navigation } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Step3Result = ({ selectedKit, gardenSize, routingData, onBack, leadId }) => {
  const [allShops, setAllShops] = useState([]);

  const routingType = routingData?.routing_type;
  const dealer = routingData?.dealer || null;
  const onlineShops = routingData?.online_shops || [];

  useEffect(() => {
    if (selectedKit?.id) {
      axios.get(`${API_URL}/api/leads/online-shops/${selectedKit.id}`)
        .then(res => setAllShops(res.data))
        .catch(() => setAllShops(onlineShops));
    }
  }, [selectedKit?.id]);

  const shopsToShow = allShops.length > 0 ? allShops : onlineShops;

  const trackClick = (targetName, targetType, url) => {
    if (leadId) {
      axios.post(`${API_URL}/api/leads/track-click`, {
        lead_id: leadId, target_name: targetName, target_type: targetType, url
      }).catch(() => {});
    }
  };

  return (
    <div data-testid="result-page" className="max-w-2xl mx-auto text-center mt-6">
      <div className="bg-stocker-green/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-5">
        <CheckCircle2 className="w-12 h-12 text-stocker-green" />
      </div>
      
      <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2 uppercase tracking-wide">
        Finalmente senza zanzare
      </h1>
      <p className="text-gray-600 text-sm mb-6">
        {routingType === 'physical_dealer' && dealer
          ? <>Per il tuo <strong>{selectedKit?.name}</strong> (~{gardenSize?.toLocaleString()} m&sup2;), il rivenditore pi&ugrave; vicino ti contatter&agrave; entro 24-48 ore.</>
          : <>Il tuo <strong>{selectedKit?.name}</strong> &egrave; disponibile presso questi rivenditori:</>
        }
      </p>

      {/* Physical Dealer Card */}
      {routingType === 'physical_dealer' && dealer && (
        <div data-testid="dealer-card" className="bg-white border-2 border-stocker-green/30 rounded-xl p-5 mb-5 text-left">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-stocker-green/10 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-stocker-green" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900">{dealer.name}</h3>
              {dealer.distance_km && (
                <p className="text-xs text-stocker-green font-medium flex items-center gap-1 mb-1.5">
                  <Navigation className="w-3 h-3" /> {dealer.distance_km} km da te
                </p>
              )}
              {(dealer.address || dealer.city || dealer.provincia) && (
                <p className="text-xs text-gray-600 flex items-start gap-1.5 mb-1"><MapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" /> <span>
                  {dealer.address && <>{dealer.address}</>}
                  {dealer.address && (dealer.city || dealer.postcode || dealer.provincia) && <>, </>}
                  {dealer.postcode && <>{dealer.postcode} </>}
                  {dealer.city && <>{dealer.city}</>}
                  {dealer.provincia && <> ({dealer.provincia})</>}
                </span></p>
              )}
              {dealer.phone && (
                <a href={`tel:${dealer.phone}`} onClick={() => trackClick(dealer.name, 'physical_dealer', `tel:${dealer.phone}`)} className="text-xs text-gray-700 hover:text-stocker-green flex items-center gap-1.5 mb-1"><Phone className="w-3 h-3 text-gray-400" /> {dealer.phone}</a>
              )}
              {dealer.email && (
                <a href={`mailto:${dealer.email}`} onClick={() => trackClick(dealer.name, 'physical_dealer', `mailto:${dealer.email}`)} className="text-xs text-gray-700 hover:text-stocker-green flex items-center gap-1.5 mb-1"><Mail className="w-3 h-3 text-gray-400" /> {dealer.email}</a>
              )}
              {dealer.website && (
                <a href={dealer.website} target="_blank" rel="noopener noreferrer" onClick={() => trackClick(dealer.name, 'physical_dealer', dealer.website)} className="text-xs text-stocker-green hover:underline flex items-center gap-1.5"><ExternalLink className="w-3 h-3" /> {dealer.website}</a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Online Shops — only when NO physical dealer found */}
      {routingType !== 'physical_dealer' && shopsToShow.length > 0 && (
        <div className="mb-5">
          <div className="space-y-2.5">
            {shopsToShow.map((shop, idx) => (
              <a
                key={shop.shop_id || idx}
                href={shop.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`online-shop-${idx}`}
                onClick={() => shop.link && trackClick(shop.name, 'online_shop', shop.link)}
                className={`flex items-center gap-3 bg-white border-2 rounded-xl p-3.5 transition-colors group ${shop.link ? 'border-gray-200 hover:border-stocker-green cursor-pointer' : 'border-gray-100 opacity-60 cursor-default'}`}
              >
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-100">
                  {shop.logo_url ? (
                    <img src={shop.logo_url} alt={shop.name} className="w-8 h-8 object-contain" />
                  ) : (
                    <ShoppingCart className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm text-gray-900 group-hover:text-stocker-green">{shop.name}</p>
                  <p className="text-[11px] text-gray-400">{shop.link ? 'Acquista ora' : 'Link in arrivo'}</p>
                </div>
                {shop.link && <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-stocker-green flex-shrink-0" />}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Kit info */}
      <div className="bg-gray-50 rounded-xl p-4 mb-5">
        <p className="text-sm text-gray-600">{selectedKit?.name}</p>
        <p className="text-stocker-green font-bold">{selectedKit?.coverage?.toLocaleString()} m&sup2;</p>
        {selectedKit?.price && (
          <p className="text-red-600 font-extrabold text-xl">&euro; {selectedKit?.price?.toLocaleString()}</p>
        )}
      </div>

      {/* Warning if kit coverage is below garden size */}
      {selectedKit?.coverage && gardenSize && selectedKit.coverage < gardenSize && (
        <div data-testid="kit-undersized-warning" className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-5 flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-amber-600 text-lg font-bold">!</span>
          </div>
          <div className="text-left">
            <p className="font-bold text-amber-800 text-sm">Attenzione: copertura insufficiente</p>
            <p className="text-amber-700 text-xs mt-1">
              Il kit scelto copre <strong>{selectedKit.coverage.toLocaleString()} m&sup2;</strong> ma il tuo giardino misura <strong>~{gardenSize.toLocaleString()} m&sup2;</strong>.
              Ti consigliamo un kit con copertura maggiore per una protezione completa.
            </p>
          </div>
        </div>
      )}

      {routingType === 'physical_dealer' && dealer && (
        <p className="text-xs text-gray-400 mb-4">
          Il rivenditore ha gi&agrave; ricevuto i tuoi dati e ti contatter&agrave; per guidarti nella scelta finale.
        </p>
      )}

      {/* Info bullets */}
      <div className="bg-stocker-green/5 border border-stocker-green/20 rounded-xl p-4 mb-5 text-left">
        <ul className="space-y-2.5">
          <li className="flex items-start gap-2.5 text-sm text-gray-700">
            <CheckCircle2 className="w-4 h-4 text-stocker-green flex-shrink-0 mt-0.5" />
            <span>Riceverai una mail di riepilogo della tua scelta con il kit consigliato</span>
          </li>
          <li className="flex items-start gap-2.5 text-sm text-gray-700">
            <CheckCircle2 className="w-4 h-4 text-stocker-green flex-shrink-0 mt-0.5" />
            <span>La rivendita pi&ugrave; vicina ti contatter&agrave; per darti una mano</span>
          </li>
        </ul>
      </div>

      {/* Share */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
        <h3 className="font-bold text-gray-900 text-sm mb-2">Condividi con chi ha un giardino!</h3>
        <div className="flex justify-center gap-3">
          <a href={`https://wa.me/?text=${encodeURIComponent(`Ciao,\nanche tu hai problemi con le zanzare? Con questo tool ho calcolato perfettamente il kit giusto per le mie esigenze. Prova anche tu a disegnare il tuo giardino e scopri la soluzione che funziona veramente contro le zanzare!\n\nhttps://start.stockergarden.com/calcola-preventivo`)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold rounded-lg text-sm">
            WhatsApp
          </a>
          <a href={`mailto:?subject=${encodeURIComponent('Scopri come proteggerti dalle zanzare')}&body=${encodeURIComponent(`Ciao,\n\nanche tu hai problemi con le zanzare? Con questo tool ho calcolato perfettamente il kit giusto per le mie esigenze. Prova anche tu a disegnare il tuo giardino e scopri la soluzione che funziona veramente contro le zanzare!\n\nhttps://start.stockergarden.com/calcola-preventivo`)}`} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg text-sm flex items-center gap-1">
            <Mail className="w-4 h-4" /> Email
          </a>
        </div>
      </div>

      <button onClick={onBack} className="text-sm text-gray-500 hover:text-stocker-green transition-colors">
        <ChevronLeft className="w-4 h-4 inline mr-1" />Torna alla selezione kit
      </button>
    </div>
  );
};

export default Step3Result;
